import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import UnsavedChangesDialog from "../../components/Common/UnsavedChangesDialog";
import InspectionCycleBreadcrumbs from "../InspectionCycles/InspectionCycleBreadcrumbs";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import { useUnsavedChanges } from "../../helpers/unsaved-changes";
import {
  getClientTypes,
  getEngagementDetail,
  getIndustryTypes,
  getInspectionCycle,
  getWorkTypes,
  getUsersList,
  saveEngagementDraft,
  searchClients,
  searchInspectionCycles,
  updateEngagementBasic,
} from "../../helpers/backend_helper";
import "./EngagementCreatePage.scss";

const editableFields = [
  ["engagementPartner", "Partner", "person"],
  ["engagementManager", "Engagement Manager", "person"],
  ["seniorReviewer", "Senior Reviewer", "person"],
  ["additionalReviewers", "Additional Reviewers", "people"],
  ["observer", "Observers", "people"],
  ["description", "Description", "textarea"],
  ["scopeDescription", "Scope Description", "textarea"],
  ["scopeExclusions", "Scope Exclusions", "textarea"],
  ["dueDate", "Due Date", "datetime-local"],
  ["inspectionStartDate", "Inspection Start Date", "datetime-local"],
  ["inspectionEndDate", "Inspection End Date", "datetime-local"],
  ["expectedCompletionDate", "Expected Completion Date", "datetime-local"],
  ["planningMeetingDate", "Planning Meeting Date", "datetime-local"],
  ["priority", "Priority", "number"],
  ["riskNewClient", "Risk: New Client", "risk"],
  ["riskNewPartner", "Risk: New Partner", "risk"],
  ["riskComplexTransaction", "Risk: Complex Transaction", "risk"],
  ["riskGrowingConcern", "Risk: Going Concern", "risk"],
  ["riskHighRiskIndustry", "Risk: High Risk Industry", "risk"],
  ["riskPriorYearAdjustments", "Risk: Prior Year Adjustments", "risk"],
  ["riskFederalFunding", "Risk: Federal Funding", "risk"],
  ["riskPriorInspectionFindings", "Risk: Prior Inspection Findings", "risk"],
  ["riskPublicCompany", "Risk: Public Company", "risk"],
  ["riskOtherRisks", "Risk: Other Risks", "risk"],
];

const parsePeople = (value) => {
  if (Array.isArray(value)) return value.map(String);
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String);
  } catch {}
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const withCurrentOption = (items, id, label) => {
  const options = [...(items || [])];
  if (id && !options.some((item) => Number(item.id) === Number(id))) {
    options.push({ id, name: label || "Current selection #" + id });
  }
  return options;
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? "#6a91ea" : "#cfd9ea",
    borderRadius: 6,
    boxShadow: state.isFocused ? "0 0 0 2px rgba(63, 112, 224, .12)" : "none",
    minHeight: 38,
  }),
  menu: (base) => ({ ...base, zIndex: 10 }),
};

export default function EngagementForm({
  engagementId: suppliedEngagementId,
  initialInspectionCycleId = 0,
  deferCycleAssignment = false,
  embedded = false,
  onSaved,
  onCancel,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { engagementId: routeEngagementId } = useParams();
  const engagementId = suppliedEngagementId || routeEngagementId;
  const isExisting = Boolean(engagementId);
  const isPreview = isExisting && location.pathname.endsWith("/preview");
  const [engagement, setEngagement] = useState(null);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [clientTypes, setClientTypes] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [inspectionCycles, setInspectionCycles] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [saving, setSaving] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  useEffect(() => {
    let current = true;
    Promise.all([
      searchClients(),
      getUsersList(),
      getClientTypes(),
      getIndustryTypes(),
      getWorkTypes(),
      searchInspectionCycles({ projectTypeId: 0, projectStatusId: 0 }),
      isExisting
        ? getEngagementDetail(engagementId)
        : Promise.resolve({ status: true, data: null }),
    ])
      .then(
        async ([
          clientsResponse,
          usersResponse,
          clientTypesResponse,
          industriesResponse,
          workTypesResponse,
          cyclesResponse,
          engagementResponse,
        ]) => {
          if (!current) return;
          if (!clientsResponse?.status)
            throw new Error(
              clientsResponse?.message || "Unable to load clients."
            );
          if (!clientTypesResponse?.status)
            throw new Error(
              clientTypesResponse?.message || "Unable to load client types."
            );
          if (!industriesResponse?.status)
            throw new Error(
              industriesResponse?.message || "Unable to load industries."
            );
          if (!workTypesResponse?.status)
            throw new Error(
              workTypesResponse?.message || "Unable to load work types."
            );
          if (!cyclesResponse?.status)
            throw new Error(
              cyclesResponse?.message || "Unable to load inspection cycles."
            );
          if (!engagementResponse?.status)
            throw new Error(
              engagementResponse?.message || "Unable to load the engagement."
            );
          const availableCycles = [...(cyclesResponse.data || [])];
          const linkedCycleId = engagementResponse.data?.inspectionCycleId || initialInspectionCycleId;
          if (
            linkedCycleId &&
            !availableCycles.some(
              (cycle) => Number(cycle.id) === Number(linkedCycleId)
            )
          ) {
            const linkedCycle = await getInspectionCycle(linkedCycleId).catch(
              () => null
            );
            availableCycles.push(
              linkedCycle?.status && linkedCycle.data
                ? linkedCycle.data
                : {
                    id: linkedCycleId,
                    title: `Inspection Cycle #${linkedCycleId} (unavailable)`,
                  }
            );
          }
          if (!current) return;
          if (!usersResponse?.status)
            throw new Error(
              usersResponse?.message || "Unable to load team members."
            );
          setUsers(usersResponse.data || []);
          const currentEngagement = engagementResponse.data;
          setClients(
            withCurrentOption(
              clientsResponse.data,
              currentEngagement?.clientId,
              currentEngagement?.client?.name ||
                currentEngagement?.client?.clientName
            )
          );
          setClientTypes(
            withCurrentOption(
              clientTypesResponse.data,
              currentEngagement?.clientTypeId,
              currentEngagement?.clientType?.name
            )
          );
          setIndustryTypes(
            withCurrentOption(
              industriesResponse.data,
              currentEngagement?.industryId,
              currentEngagement?.industryType?.name
            )
          );
          setWorkTypes(
            withCurrentOption(
              workTypesResponse.data,
              currentEngagement?.workTypeId,
              currentEngagement?.workType?.name
            )
          );
          setInspectionCycles(availableCycles);
          setEngagement(engagementResponse.data || null);
        }
      )
      .catch((error) =>
        ErrorNotify(error.message || "Unable to load engagement options.")
      )
      .finally(() => {
        if (current) setLoadingOptions(false);
      });
    return () => {
      current = false;
    };
  }, [engagementId, isExisting, initialInspectionCycleId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...Object.fromEntries(
        editableFields.map(([key, , type]) => [
          key,
          type === "people"
            ? parsePeople(engagement?.[key])
            : type === "risk"
            ? engagement?.[key] || "N"
            : type === "datetime-local"
            ? engagement?.[key]?.slice(0, 19) || ""
            : engagement?.[key] ?? (type === "number" ? 0 : ""),
        ])
      ),
      name: engagement?.name || "",
      finYearEnd: engagement?.finYearEnd || "",
      clientId: engagement?.clientId ? String(engagement.clientId) : "",
      newClientName: "",
      clientTypeId: engagement?.clientTypeId
        ? String(engagement.clientTypeId)
        : "",
      industryId: engagement?.industryId ? String(engagement.industryId) : "",
      workTypeId: engagement?.workTypeId ? String(engagement.workTypeId) : "",
      inspectionCycleId: engagement?.inspectionCycleId
        ? String(engagement.inspectionCycleId)
        : initialInspectionCycleId ? String(initialInspectionCycleId) : "",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required("Enter an engagement name."),
      finYearEnd: Yup.string().trim().required("Enter the financial year end."),
      clientId: Yup.string().when("newClientName", {
        is: (value) => !String(value || "").trim(),
        then: (schema) =>
          schema.required("Select an existing client or create a new client."),
      }),
      newClientName: Yup.string()
        .trim()
        .max(250, "Client name must be 250 characters or fewer."),
      clientTypeId: Yup.string().required(
        "The selected client must have a client type."
      ),
      industryId: Yup.string().required(
        "The selected client must have an industry type."
      ),
      workTypeId: Yup.string().required("Select a work type."),
      inspectionCycleId: isExisting
        ? Yup.string()
        : Yup.string().required("Select an inspection cycle."),
      priority: Yup.number()
        .integer("Priority must be a whole number.")
        .min(0, "Priority cannot be negative."),
    }),
    onSubmit: async (values) => {
      if (saving) return;
      setSaving(true);
      try {
        const basicValues = {
          name: values.name.trim(),
          finYearEnd: values.finYearEnd.trim(),
          clientId: values.clientId ? Number(values.clientId) : null,
          newClientName: values.newClientName.trim() || null,
          clientTypeId: Number(values.clientTypeId),
          industryId: Number(values.industryId),
          workTypeId: Number(values.workTypeId),
          inspectionCycleId: deferCycleAssignment ? 0 : Number(values.inspectionCycleId) || 0,
        };
        const response = isExisting
          ? await updateEngagementBasic({
              id: Number(engagementId),
              expectedRowVersion: engagement?.rowVersion || null,
              properties: Object.fromEntries(
                editableFields.map(([key, , type]) => [
                  key,
                  type === "datetime-local"
                    ? values[key] || null
                    : type === "number"
                    ? Number(values[key])
                    : values[key],
                ])
              ),
              ...basicValues,
            })
          : await saveEngagementDraft({
              id: 0,
              ...basicValues,
              engagementPartner: "",
              engagementManager: "",
              seniorReviewer: "",
              additionalReviewers: [],
              observer: [],
              reviewTemplateId: 0,
              inspectionTypeId: null,
              checklistId: 0,
              checklistIds: [],
              checklistAssignments: [],
              reviewConfigurations: [],
              templateRoleAssignments: [],
              riskAssignments: [],
              step: 0,
            });
        if (!response?.status || !response.data?.id) {
          const validationMessage = Object.values(response?.errors || {})
            .flat()
            .join(" ");
          throw new Error(
            validationMessage ||
              response?.message ||
              `Unable to ${isExisting ? "update" : "create"} the engagement.`
          );
        }
        validation.resetForm({ values });
        if (onSaved) {
          SuccessNotify(`Engagement ${isExisting ? "updated" : "created"} successfully.`);
          onSaved(response.data);
          return;
        }
        if (isExisting) {
          SuccessNotify("Engagement updated successfully.");
          navigate("/engagements/list");
        } else {
          SuccessNotify("Engagement created successfully.");
          navigate("/engagements/list");
        }
      } catch (error) {
        ErrorNotify(
          error.message ||
            `Unable to ${isExisting ? "update" : "create"} the engagement.`
        );
      } finally {
        setSaving(false);
      }
    },
  });

  useUnsavedChanges(validation.dirty && !isPreview);

  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        value: String(client.id),
        label: client.name || client.clientName,
      })),
    [clients]
  );
  const selectedClientType =
    clientTypes.find(
      (item) => String(item.id) === validation.values.clientTypeId
    )?.name || "";
  const selectedIndustry =
    industryTypes.find(
      (item) => String(item.id) === validation.values.industryId
    )?.name || "";

  const selectClient = (option) => {
    const clientId = option?.value || "";
    const client = clients.find((item) => String(item.id) === clientId);
    validation.setFieldValue("clientId", clientId);
    validation.setFieldValue("newClientName", "");
    validation.setFieldValue(
      "clientTypeId",
      client?.clientTypeId ? String(client.clientTypeId) : ""
    );
    validation.setFieldValue(
      "industryId",
      client?.industryTypeId ? String(client.industryTypeId) : ""
    );
  };

  const selectNewClient = (inputValue) => {
    const clientName = String(inputValue || "").trim();
    if (!clientName) return;
    validation.setFieldValue("clientId", "");
    validation.setFieldValue("newClientName", clientName);
    validation.setFieldValue("clientTypeId", "");
    validation.setFieldValue("industryId", "");
    validation.setFieldTouched("clientId", false);
  };

  const requestCancel = () =>
    validation.dirty && !isPreview
      ? setDiscardOpen(true)
      : onCancel ? onCancel() : navigate("/engagements/list");

  const cycleOptions = useMemo(
    () =>
      inspectionCycles.map((cycle) => ({
        value: String(cycle.id),
        label: [
          cycle.projectNo,
          cycle.title || cycle.projectTitle || cycle.name,
        ]
          .filter(Boolean)
          .join(" — "),
      })),
    [inspectionCycles]
  );

  return (
    <main className={`engagement-create-page piers-page${embedded ? " engagement-create-page--embedded" : ""}`}>
      {!embedded && <header className="engagement-create-page__header piers-page-header">
        <InspectionCycleBreadcrumbs
          items={[
            { label: "Engagements", to: "/engagements/list" },
            {
              label: isPreview
                ? "Preview Engagement"
                : isExisting
                ? "Edit Engagement"
                : "Create New Engagement",
            },
          ]}
          onNavigate={(target) =>
            validation.dirty && !isPreview
              ? setDiscardOpen(true)
              : navigate(target)
          }
        />
        <h1 className="piers-page-title">
          {isPreview
            ? "Engagement Preview"
            : isExisting
            ? "Edit Engagement"
            : "New Engagement"}
        </h1>
        <p>
          {isPreview
            ? "Review the engagement's basic information."
            : isExisting
            ? "Update the engagement's properties."
            : "Provide the basic information to create an engagement. Inspection setup can be completed afterwards."}
        </p>
      </header>}
      <section
        className="engagement-create-page__surface"
        aria-label={
          isPreview
            ? "Engagement preview"
            : isExisting
            ? "Edit engagement form"
            : "New engagement form"
        }
      >
        <Form onSubmit={validation.handleSubmit}>
          <div className="engagement-create-page__section-title">
            <i className="bx bx-briefcase-alt-2" aria-hidden="true" />
            <h2>Engagement details</h2>
          </div>
          <div className="engagement-create-page__grid">
            <FormGroup className="engagement-create-page__field--wide">
              <Label>
                Engagement Name <b>*</b>
              </Label>
              <Input
                name="name"
                placeholder="Enter engagement name"
                value={validation.values.name}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={Boolean(
                  validation.touched.name && validation.errors.name
                )}
                disabled={isPreview || loadingOptions}
              />
              {validation.touched.name && validation.errors.name && (
                <FormFeedback>{validation.errors.name}</FormFeedback>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="engagement-finYearEnd">
                Financial Year End <b>*</b>
              </Label>
              <Input
                id="engagement-finYearEnd"
                name="finYearEnd"
                type={isExisting && validation.values.finYearEnd && !/^\d{4}-\d{2}-\d{2}$/.test(validation.values.finYearEnd) ? "text" : "date"}
                value={validation.values.finYearEnd}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={Boolean(
                  validation.touched.finYearEnd && validation.errors.finYearEnd
                )}
                disabled={isPreview || loadingOptions}
              />
              {validation.touched.finYearEnd &&
                validation.errors.finYearEnd && (
                  <FormFeedback>{validation.errors.finYearEnd}</FormFeedback>
                )}
            </FormGroup>
            <FormGroup>
              <Label>
                Work Type <b>*</b>
              </Label>
              <Input
                type="select"
                name="workTypeId"
                value={validation.values.workTypeId}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={Boolean(
                  validation.touched.workTypeId && validation.errors.workTypeId
                )}
                disabled={isPreview || loadingOptions}
              >
                <option value="">Select work type</option>
                {workTypes.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Input>
              {validation.touched.workTypeId &&
                validation.errors.workTypeId && (
                  <FormFeedback>{validation.errors.workTypeId}</FormFeedback>
                )}
            </FormGroup>
            <FormGroup className="engagement-create-page__field--wide">
              <Label>
                Client <b>*</b>
              </Label>
              {!isExisting && !isPreview ? (
                <CreatableSelect
                  inputId="clientId"
                  classNamePrefix="engagement-basic-client"
                  options={clientOptions}
                  value={
                    validation.values.newClientName
                      ? {
                          value: validation.values.newClientName,
                          label: validation.values.newClientName,
                          __isNew__: true,
                        }
                      : clientOptions.find(
                          (option) =>
                            option.value === validation.values.clientId
                        ) || null
                  }
                  onChange={selectClient}
                  onCreateOption={selectNewClient}
                  onBlur={() => validation.setFieldTouched("clientId", true)}
                  formatCreateLabel={(value) =>
                    `Create new client “${String(value).trim()}”`
                  }
                  isValidNewOption={(value) =>
                    Boolean(String(value).trim()) &&
                    !clients.some(
                      (client) =>
                        String(client.name || client.clientName || "")
                          .trim()
                          .toLowerCase() === String(value).trim().toLowerCase()
                    )
                  }
                  placeholder={
                    loadingOptions
                      ? "Loading clients…"
                      : "Search or enter a new client name"
                  }
                  isDisabled={loadingOptions}
                  styles={selectStyles}
                />
              ) : (
                <Select
                  inputId="clientId"
                  classNamePrefix="engagement-basic-client"
                  options={clientOptions}
                  value={
                    clientOptions.find(
                      (option) => option.value === validation.values.clientId
                    ) || null
                  }
                  onChange={selectClient}
                  onBlur={() => validation.setFieldTouched("clientId", true)}
                  placeholder={
                    loadingOptions
                      ? "Loading clients…"
                      : "Search and select client"
                  }
                  isDisabled={isPreview || loadingOptions}
                  styles={selectStyles}
                />
              )}
              {validation.values.newClientName && (
                <small className="engagement-create-page__new-client-note">
                  <i className="bx bx-plus-circle" /> A new client named “
                  {validation.values.newClientName}” will be created with this
                  engagement.
                </small>
              )}
              {validation.touched.clientId && validation.errors.clientId && (
                <FormFeedback className="d-block">
                  {validation.errors.clientId}
                </FormFeedback>
              )}
            </FormGroup>
            <FormGroup className="engagement-create-page__field--wide">
              <Label htmlFor="engagementInspectionCycleId">
                Inspection Cycle{" "}
                {isExisting ? (
                  <span className="engagement-create-page__optional">(optional)</span>
                ) : (
                  <b>*</b>
                )}
              </Label>
              <Select
                isDisabled={deferCycleAssignment || isPreview || loadingOptions}
                inputId="engagementInspectionCycleId"
                aria-required={!isExisting}
                aria-invalid={Boolean(
                  validation.touched.inspectionCycleId &&
                    validation.errors.inspectionCycleId
                )}
                aria-describedby="engagementInspectionCycleId-error"
                classNamePrefix="engagement-basic-cycle"
                options={cycleOptions}
                value={
                  cycleOptions.find(
                    (option) =>
                      option.value === validation.values.inspectionCycleId
                  ) || null
                }
                onChange={(option) =>
                  validation.setFieldValue(
                    "inspectionCycleId",
                    option?.value || ""
                  )
                }
                onBlur={() =>
                  validation.setFieldTouched("inspectionCycleId", true)
                }
                placeholder={
                  loadingOptions
                    ? "Loading inspection cycles…"
                    : "Search and select an inspection cycle"
                }
                isClearable
                styles={selectStyles}
              />
              {validation.touched.inspectionCycleId &&
                validation.errors.inspectionCycleId && (
                  <FormFeedback className="d-block" id="engagementInspectionCycleId-error">
                    {validation.errors.inspectionCycleId}
                  </FormFeedback>
                )}
            </FormGroup>
            <FormGroup>
              <Label>
                Client Type <b>*</b>
              </Label>
              {validation.values.newClientName || (isExisting && !isPreview) ? (
                <Input
                  type="select"
                  name="clientTypeId"
                  value={validation.values.clientTypeId}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={Boolean(
                    validation.touched.clientTypeId &&
                      validation.errors.clientTypeId
                  )}
                  disabled={isPreview || loadingOptions}
                >
                  <option value="">Select client type</option>
                  {clientTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Input>
              ) : (
                <Input
                  value={selectedClientType}
                  placeholder="Select a client first"
                  disabled
                  invalid={Boolean(
                    validation.touched.clientTypeId &&
                      validation.errors.clientTypeId
                  )}
                />
              )}
              {validation.touched.clientTypeId &&
                validation.errors.clientTypeId && (
                  <FormFeedback>{validation.errors.clientTypeId}</FormFeedback>
                )}
            </FormGroup>
            <FormGroup>
              <Label>
                Industry Type <b>*</b>
              </Label>
              {validation.values.newClientName || (isExisting && !isPreview) ? (
                <Input
                  type="select"
                  name="industryId"
                  value={validation.values.industryId}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={Boolean(
                    validation.touched.industryId &&
                      validation.errors.industryId
                  )}
                  disabled={isPreview || loadingOptions}
                >
                  <option value="">Select industry type</option>
                  {industryTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Input>
              ) : (
                <Input
                  value={selectedIndustry}
                  placeholder="Select a client first"
                  disabled
                  invalid={Boolean(
                    validation.touched.industryId &&
                      validation.errors.industryId
                  )}
                />
              )}
              {validation.touched.industryId &&
                validation.errors.industryId && (
                  <FormFeedback>{validation.errors.industryId}</FormFeedback>
                )}
            </FormGroup>
          </div>
          {isExisting && (
            <>
              <div className="engagement-create-page__section-title">
                <h2>Team, scope, dates and risk</h2>
              </div>
              <div className="engagement-create-page__grid">
                {editableFields.map(([key, label, type]) => {
                  const options = users.map((user) => ({
                    value: String(user.id),
                    label:
                      user.fullName ||
                      user.userName ||
                      user.emailId ||
                      user.name ||
                      user.email ||
                      String(user.id),
                  }));
                  const selected =
                    type === "people"
                      ? validation.values[key]
                      : [String(validation.values[key] || "")].filter(Boolean);
                  selected.forEach((value) => {
                    if (!options.some((option) => option.value === value))
                      options.push({ value, label: value + " (current)" });
                  });
                  return (
                    <FormGroup
                      key={key}
                      className={
                        type === "textarea"
                          ? "engagement-create-page__field--wide"
                          : ""
                      }
                    >
                      <Label htmlFor={"engagement-" + key}>{label}</Label>
                      {type === "person" || type === "people" ? (
                        <Select
                          inputId={"engagement-" + key}
                          options={options}
                          isMulti={type === "people"}
                          isClearable
                          value={
                            type === "people"
                              ? options.filter((option) =>
                                  selected.includes(option.value)
                                )
                              : options.find((option) =>
                                  selected.includes(option.value)
                                ) || null
                          }
                          onChange={(option) =>
                            validation.setFieldValue(
                              key,
                              type === "people"
                                ? (option || []).map((item) => item.value)
                                : option?.value || ""
                            )
                          }
                          isDisabled={isPreview || loadingOptions}
                          styles={selectStyles}
                        />
                      ) : type === "risk" ? (
                        <Input
                          id={"engagement-" + key}
                          name={key}
                          type="select"
                          value={validation.values[key]}
                          onChange={validation.handleChange}
                          disabled={isPreview || loadingOptions}
                        >
                          <option value="N">No</option>
                          <option value="Y">Yes</option>
                        </Input>
                      ) : (
                        <Input
                          id={"engagement-" + key}
                          name={key}
                          type={type}
                          step={type === "datetime-local" ? 1 : undefined}
                          min={type === "number" ? 0 : undefined}
                          value={validation.values[key]}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          disabled={isPreview || loadingOptions}
                        />
                      )}
                      {validation.touched[key] && validation.errors[key] && (
                        <FormFeedback className="d-block">
                          {validation.errors[key]}
                        </FormFeedback>
                      )}
                    </FormGroup>
                  );
                })}
              </div>
              <div className="engagement-create-page__section-title">
                <h2>System information</h2>
              </div>
              <div className="engagement-create-page__grid">
                {[
                  ["engagementNo", "Engagement ID"],
                  ["createdOn", "Created On"],
                  ["updatedOn", "Last Updated"],
                  ["completedOn", "Completed On"],
                  ["percentage", "Progress (%)"],
                ].map(([key, label]) => (
                  <FormGroup key={key}>
                    <Label htmlFor={"system-" + key}>{label}</Label>
                    <Input
                      id={"system-" + key}
                      value={engagement?.[key] || ""}
                      readOnly
                    />
                  </FormGroup>
                ))}
              </div>
            </>
          )}
          <footer className="engagement-create-page__footer">
            {isPreview ? (
              <>
                <Button
                  type="button"
                  color="light"
                  onClick={() => navigate("/engagements/list")}
                >
                  Back
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  color="light"
                  onClick={requestCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={
                    saving || loadingOptions || (isExisting && !engagement)
                  }
                >
                  {saving && (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    />
                  )}
                  {saving
                    ? isExisting
                      ? "Saving…"
                      : "Creating…"
                    : isExisting
                    ? "Save Changes"
                    : "Create Engagement"}
                </Button>
              </>
            )}
          </footer>
        </Form>
      </section>
      <UnsavedChangesDialog
        isOpen={discardOpen}
        onContinue={() => setDiscardOpen(false)}
        onDiscard={() => onCancel ? onCancel() : navigate("/engagements/list")}
        subject="engagement details"
      />
    </main>
  );
}
